// Structured Data for SEO & GEO (Generative Engine Optimization)

type JsonObject = Record<string, unknown>;

interface BlogPostStructuredDataInput {
  title: string;
  description: string;
  slug: string;
  categorySlug: string;
  coverImageUrl?: string;
  createdAt: string;
  updatedAt?: string;
  authorName?: string;
  authorImageUrl?: string;
  keywords?: string;
  wordCount?: number;
}

interface ProductStructuredDataInput {
  title: string;
  description?: string;
  slug: string;
  imageUrl?: string;
  price?: number;
  offerPrice?: number;
  currency?: string;
  currencySymbol?: string;
  inStock?: boolean;
  brand?: string;
}

interface PodcastEpisodeStructuredDataInput {
  title: string;
  description?: string;
  slug: string;
  thumbnailUrl?: string;
  audioFileUrl?: string;
  createdAt: string;
  episodeNumber?: number;
  duration?: string;
}

export function generateBreadcrumbStructuredData(pathnames: string[]): JsonObject {
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://www.harivishnu.in/"
    }
  ];

  let currentPath = "https://www.harivishnu.in";

  pathnames.forEach((name, index) => {
    currentPath += `/${name}`;
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": index + 2,
      "name": name.charAt(0).toUpperCase() + name.slice(1),
      "item": currentPath
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems
  };
}

export function generateWebsiteStructuredData(): JsonObject {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Hari Vishnu",
    "url": "https://www.harivishnu.in",
    "description": "Personal Blog, Podcast, Projects & Portfolio by Hari Vishnu",
    "inLanguage": "en-IN",
    "author": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in/contact",
      "sameAs": [
        "https://twitter.com/Im_Harivishnu",
        "https://instagram.com/im_harivishnu",
        "https://orcid.org/0009-0009-4657-0766",
        "https://scholar.google.com/citations?hl=en&user=4mOxTrAAAAAJ"
      ]
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.harivishnu.in/blog?search={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };
}

export function generateOrganizationStructuredData(): JsonObject {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Hari Vishnu",
    "url": "https://www.harivishnu.in",
    "image": "https://www.harivishnu.in/images/home_images/Logo_Transparent.png",
    "description": "Blogger, Podcaster, and Tech Enthusiast sharing insights on technology, business, and personal growth.",
    "sameAs": [
      "https://twitter.com/Im_Harivishnu",
      "https://instagram.com/im_harivishnu",
      "https://www.threads.net/@im_harivishnu",
      "https://orcid.org/0009-0009-4657-0766",
      "https://scholar.google.com/citations?hl=en&user=4mOxTrAAAAAJ"
    ],
    "knowsAbout": ["Technology", "Business", "Artificial Intelligence", "Programming", "Personal Development"],
    "worksFor": {
      "@type": "Organization",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in"
    }
  };
}

export function generateBlogPostStructuredData({ title, description, slug, categorySlug, coverImageUrl, createdAt, updatedAt, authorName, authorImageUrl, keywords, wordCount }: BlogPostStructuredDataInput): JsonObject {
  const url = `https://blog.harivishnu.in/${categorySlug}/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": coverImageUrl || "https://www.harivishnu.in/images/home_images/Logo.png",
    "url": url,
    "datePublished": createdAt,
    "dateModified": updatedAt || createdAt,
    "inLanguage": "en-IN",
    "author": {
      "@type": "Person",
      "name": authorName || "Hari Vishnu",
      "image": authorImageUrl || "https://www.harivishnu.in/images/home_images/Logo.png",
      "url": "https://www.harivishnu.in/contact"
    },
    "publisher": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.harivishnu.in/images/home_images/Logo_Transparent.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "isPartOf": {
      "@type": "Blog",
      "name": "Hari Vishnu Blog",
      "url": "https://blog.harivishnu.in"
    },
    ...(keywords ? { "keywords": keywords } : {}),
    ...(wordCount ? { "wordCount": wordCount } : {})
  };
}

export function generateProductStructuredData({ title, description, slug, imageUrl, price, offerPrice, currency, currencySymbol, inStock = true, brand }: ProductStructuredDataInput): JsonObject {
  const productUrl = `https://store.harivishnu.in/${slug}`;
  const displayPrice = offerPrice || price;
  const priceCurrency = currency || "INR";

  const schema: { offers: Record<string, unknown> } & JsonObject = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": title,
    "description": description || `Buy ${title} from Hari Vishnu Store.`,
    "url": productUrl,
    "image": imageUrl || "https://www.harivishnu.in/images/home_images/Logo.png",
    "brand": {
      "@type": "Brand",
      "name": brand || "Hari Vishnu"
    },
    "offers": {
      "@type": "Offer",
      "url": productUrl,
      "price": displayPrice,
      "priceCurrency": priceCurrency,
      "availability": inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "seller": {
        "@type": "Person",
        "name": "Hari Vishnu"
      }
    }
  };

  return schema;
}

export function generatePodcastEpisodeStructuredData({ title, description, slug, thumbnailUrl, audioFileUrl, createdAt, episodeNumber, duration }: PodcastEpisodeStructuredDataInput): JsonObject {
  const episodeUrl = `https://podcast.harivishnu.in/${slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    "name": title,
    "description": description || `Listen to ${title} on Hari Vishnu Insights Podcast.`,
    "url": episodeUrl,
    "image": thumbnailUrl || "https://www.harivishnu.in/images/home_images/Logo.png",
    "datePublished": createdAt,
    "inLanguage": "en-IN",
    "author": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in/contact"
    },
    "partOfSeries": {
      "@type": "PodcastSeries",
      "name": "Hari Vishnu Insights",
      "url": "https://podcast.harivishnu.in",
      "image": "https://www.harivishnu.in/images/home_images/Logo.png"
    },
    ...(audioFileUrl ? {
      "associatedMedia": {
        "@type": "MediaObject",
        "contentUrl": audioFileUrl,
        "encodingFormat": "audio/mpeg",
        ...(duration ? { "duration": duration } : {})
      }
    } : {}),
    ...(episodeNumber ? { "episodeNumber": episodeNumber } : {})
  };
}

export function generatePodcastSeriesStructuredData(): JsonObject {
  return {
    "@context": "https://schema.org",
    "@type": "PodcastSeries",
    "name": "Hari Vishnu Insights",
    "description": "A podcast by Hari Vishnu featuring inspiring conversations, expert interviews, and stories from the world of technology, creativity, and personal development.",
    "url": "https://podcast.harivishnu.in",
    "image": "https://www.harivishnu.in/images/home_images/Logo.png",
    "inLanguage": "en-IN",
    "author": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in/contact"
    }
  };
}

export function generateStoreStructuredData(): JsonObject {
  return {
    "@context": "https://schema.org",
    "@type": "Store",
    "name": "Hari Vishnu Store",
    "description": "Shop curated books, digital products, and merchandise by Hari Vishnu.",
    "url": "https://store.harivishnu.in",
    "image": "https://www.harivishnu.in/images/home_images/Logo.png",
    "seller": {
      "@type": "Person",
      "name": "Hari Vishnu",
      "url": "https://www.harivishnu.in"
    },
    "currenciesAccepted": "INR",
    "paymentAccepted": "Credit Card, Debit Card, UPI, Net Banking"
  };
}

